import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { css } from "@emotion/css";
import { TextField } from "@design-system/TextField";
import { Button } from "@design-system/Button";
import { SignInUpTitle } from "@components/SignInUpTitle";
import { Spacing } from "@components/Spacing";
import { useLoading } from "@hooks/useLoading";
import { ErrorMessage } from "@components/ErrorMessage";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { isValidEmail } from "@utils/validation";
import { useAuth } from "@contexts/AuthContext";
import { ReactivateAccountModal } from "@components/ReactivateAccountModal";
import GoogleLoginButton from "@components/GoogleLogin";
import axios from "axios";

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, reactivateAccount } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, startLoading, stopLoading] = useLoading();
  const [error, handleError, clearError, setError] = useErrorHandling();
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivateLoading, startReactivateLoading, stopReactivateLoading] = useLoading();
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [loginPayload, setLoginPayload] = useState<{email?: string; username?: string; password: string} | null>(null);

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifier(e.target.value);
    clearError();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    clearError();
  };

  const handleReactivateAccount = async () => {
    if (!loginPayload) return;
    
    startReactivateLoading();
    setReactivateError(null);
    try {
      await reactivateAccount(loginPayload);
      setShowReactivateModal(false);
      setLoginPayload(null);
      navigate("/color");
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.detail) {
        setReactivateError(e.response.data.detail);
      } else {
        setReactivateError("Failed to reactivate account. Please try again.");
      }
    } finally {
      stopReactivateLoading();
    }
  };

  const handleSubmit = async () => {
    const isEmail = isValidEmail(identifier);

    if (!isEmail && identifier.trim().length < 1) {
      return setError("Please enter a valid email or username.");
    }

    if (password.trim().length < 1) {
      return setError("Please enter your password.");
    }

    startLoading();
    try {
      const payload = {
        ...(isEmail ? { email: identifier } : { username: identifier }),
        password,
      };

      await login(payload);
      navigate("/color");
    } catch (e) {
      // Check if this is the user_deleted error
      if (axios.isAxiosError(e) && e.response?.data?.code === "user_deleted") {
        // Store the login payload for reactivation
        setLoginPayload({
          ...(isEmail ? { email: identifier } : { username: identifier }),
          password,
        });
        setShowReactivateModal(true);
        clearError();
      } else {
        handleError(e);
      }
    } finally {
      stopLoading();
    }
  };

  const isButtonDisabled =
    identifier.length < 1 || password.length < 1 || error != null;

  const googleLogin = async (code: string) => {
    try {
      await loginWithGoogle(code);
      navigate("/color");
    } catch (e) {
      // Check if this is the user_deleted error for Google login too
      if (axios.isAxiosError(e) && e.response?.data?.code === "user_deleted") {
        // For Google login, we can't reactivate since we don't have the password
        // Just show a regular error message
        handleError(new Error("Your account has been deactivated. Please use the regular login form to reactivate your account."));
      } else {
        handleError(e);
      }
    }
  };

  return (
    <div className={wrapperStyle}>
      <SignInUpTitle>Log In</SignInUpTitle>
      <Spacing size={40} />
      <TextField
        placeholder="Email or Username"
        value={identifier}
        onChange={handleIdentifierChange}
      />
      <Spacing size={20} />
      <TextField
        placeholder="Password"
        type="password"
        value={password}
        onChange={handlePasswordChange}
      />
      <Spacing size={20} />
      <Button onClick={handleSubmit} loading={loading} disabled={isButtonDisabled}>
        Log In
      </Button>
      <ErrorMessage text={error} />
      <Spacing size={20} />
      <GoogleLoginButton onSuccess={googleLogin} />
      <Spacing size={20} />
      <Link to="/sign-up" className={signUpLinkStyle}>
        Don't have an account? Sign up
      </Link>

      <ReactivateAccountModal
        isOpen={showReactivateModal}
        onReactivate={handleReactivateAccount}
        onCancel={() => {
          setShowReactivateModal(false);
          setReactivateError(null);
          setLoginPayload(null);
        }}
        loading={reactivateLoading}
        error={reactivateError}
      />
    </div>
  );
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const signUpLinkStyle = css`
  color: #0f7dff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

export default Login;
